import { set, get, find } from 'lodash';
import debug from 'debug';
import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { CardFlowEvent } from 'types/story';
import { PARAMS, IFRAME_ACTIONS, CARD_TYPE, FLOW_DEFAULT_EVENT_NAME } from 'common/constants';
import { parseURL, searchObjectToString } from 'common/utils/parse-url';
import { StoryFacade } from 'common/utils/facades/story-facade';
import { IframeTunnel } from 'common/utils/iframe-tunnel';
import { NavigationFacade } from 'common/utils/facades/navigation-card-facade';
import { isPreview, isPreviewRoute, updateStoryLastCardId } from 'client/utils';
import { sendGameCheckpoint } from 'client/actions/api';
import { ComponentEvents, EventProviderContext, EventProviderContextT } from './Context';

const log = debug('client:components:EventProvider');
const { Provider } = EventProviderContext;

const getNextCardSearchObject = ({ nextCardId }) => {
	if (!nextCardId) {
		return null;
	}

	const { searchObject } = parseURL(window.location.href);

	set(searchObject, PARAMS.CARD_ID, nextCardId);

	return searchObject;
};

type Props = ConnectedProps<typeof connector> &
	RouteComponentProps & {
		story: StoryFacade;
		isEditor: boolean;
		nextDefaultCardId: string;
		events: CardFlowEvent[];
	};

const getPointIdOf = event => get(event, 'connection.pointId');

class EventProvider extends Component<Props> {
	componentEvents: ComponentEvents = {};

	get events(): EventProviderContextT['eventProvider']['events'] {
		return {
			flow: this.callFlowEvent,
			component: this.componentEvents,
		};
	}

	setComponentEvents: EventProviderContextT['eventProvider']['setComponentEvents'] = (componentId, value) => {
		this.componentEvents[componentId] = {
			...(this.componentEvents[componentId] ?? {}),
			...value,
		};
	};

	callFlowEvent: EventProviderContextT['eventProvider']['events']['flow'] = eventId => {
		const { events, nextDefaultCardId, history, isEditor, story: storyFacade } = this.props;
		const event =
			find(events, e => e.componentId === eventId) || find(events, e => e.name === FLOW_DEFAULT_EVENT_NAME);

		// if no event or event isn't connected — navigate to next card
		let nextCardId = getPointIdOf(event) || nextDefaultCardId;

		if (!nextCardId) {
			return; // end
		}

		const { card: nextCard, step: nextStep } = storyFacade.getDataByCardId(nextCardId);

		if (nextCard && nextCard.type === CARD_TYPE.NAVIGATION && nextStep) {
			const { totalScore } = storyFacade;
			const navigationFacade = new NavigationFacade(nextCard);
			const redirectTo =
				// next card by param (eg. by score)
				navigationFacade.getNextCardId({ totalScore }) ||
				// or next step 1st card in case of default event
				this.props.story.getNextDefaultCard(nextStep)?._id;

			if (redirectTo) {
				log('Navigation: redirect to', redirectTo);
				nextCardId = redirectTo;
			} else {
				log('Navigation: redirect is not possible');
				return; // end
			}
		}

		const nextCardSearchObject = getNextCardSearchObject({ nextCardId });
		const search = searchObjectToString(nextCardSearchObject);

		log('go to', search);

		if (!isEditor && !isPreview) {
			updateStoryLastCardId(storyFacade.storyId, nextCardId);

			if (storyFacade.settings.restrictions?.oncePerUser) {
				this.props.sendGameCheckpoint(storyFacade.storyId, nextCardId);
			}
		}

		history.push({ search });

		if (!isEditor && isPreviewRoute) {
			const iframeTunnel = new IframeTunnel({ id: 'EventProvider' });
			iframeTunnel.messageTransmitter({
				target: IframeTunnel.targets.admin,
				action: IFRAME_ACTIONS.UPDATE_ADMIN_PREVIEW_LOCATION,
				payload: nextCardSearchObject,
			});
		}
	};

	render() {
		return (
			<Provider value={{ eventProvider: { events: this.events, setComponentEvents: this.setComponentEvents } }}>
				{this.props.children}
			</Provider>
		);
	}
}

const connector = connect(null, { sendGameCheckpoint });

export default withRouter(connector(EventProvider));
